import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"
import { theme } from "@common/theme/theme"
import FunnelQuestionScreen from "@common/components/funnel/FunnelQuestionScreen"
import { apiRequest } from "@data/redux/actions/api"
import objectifIllustration from "@assets/summer/funnel/objectif.png"
import niveauIllustration from "@assets/summer/funnel/niveau.png"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import { SUMMER_OBJECTIVES, SUMMER_NIVEAUX } from "./constants/summerContent"

/**
 * Étape diagnostic — "une question par écran" (template Preply via
 * <FunnelQuestionScreen />). Deux micro-engagements enchaînés, chacun sur son
 * propre écran pour entretenir le momentum (effet IKEA) :
 *   1. Objectif de l'été (mono)
 *   2. Niveau ressenti (mono)
 *
 * Les MATIÈRES ne sont plus collectées ici : elles font désormais partie de la
 * FORMULE (matières + créneaux + mois + prix), réglée ENSEMBLE à l'étape
 * configurateur — pour que le parent puisse arbitrer matières ↔ créneaux ↔ prix
 * en un seul endroit (si le prix est trop haut, il peut retirer un créneau OU une
 * matière). Le diagnostic ne porte donc que les entrées qui personnalisent le
 * CONTENU sans toucher au tarif (objectif / niveau).
 *
 * La complétion persiste les réponses (updateAnswers → auto-transition
 * `summer_diag_completed` côté backend dès objectif + niveau présents) et track
 * Amplitude, puis navigue vers le CONFIGURATEUR (matières + créneaux + mois +
 * prix). Le reveal du programme vient APRÈS le configurateur, qui dispose alors
 * des matières ET des créneaux réels → vrai programme (pas un placeholder).
 */

// Sous-étapes du diagnostic, dans l'ordre.
const STEP_OBJECTIVE = 0
const STEP_NIVEAU = 1

// Step global affiché dans la barre de progression du header. Séquence à plat :
// classe = 1, objectif = 2, niveau = 3, configurateur = 4, programme = 5,
// tuteurs = 6, réservation = 7. total = 7 (la classe + les 2 questions diag + 4
// écrans aval, dont l'étape "aha moment tuteur").
const FUNNEL_TOTAL_STEPS = 7
const DIAGNOSTIC_GLOBAL_STEP = [2, 3]

const SummerDiagnosticScreen = (props) => {
    const { navigation, reduxFunnelStateId } = props
    const { state, update } = useSummerFunnel()
    // Refresh-safe / URL-partageable : `?user=` reste dans l'URL ; cold-load
    // (refresh / URL ouverte ailleurs) ré-hydre via summer/resume, sinon retour
    // au début. Renvoie le `user` pour ne pas court-circuiter la reprise.
    const { userParam, nextParams } = useSummerFunnelGuard()

    const [subStep, setSubStep] = useState(STEP_OBJECTIVE)

    const [objective, setObjective] = useState(state.objective || null)
    const [niveauActuel, setNiveauActuel] = useState(state.niveauActuel || null)

    // Pas de classe → on renvoie à l'étape 1 (la classe pilote les matières,
    // désormais choisies au configurateur). Quand un `user` est présent sur un
    // contexte froid, la garde gère déjà la reprise (summer/resume) : on ne
    // double pas la redirection ici.
    useEffect(() => {
        if (!state.studentClass && !userParam) {
            navigation.navigate("summer/start")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Persiste le diagnostic (objectif / niveau) puis ouvre le CONFIGURATEUR
    // (matières + créneaux + mois + prix). Le reveal du programme vient ensuite.
    // On conserve la mécanique d'origine (updateAnswers + Amplitude).
    const submitDiagnostic = () => {
        update({ objective, niveauActuel })
        const stateId = state.funnelStateId || reduxFunnelStateId
        if (stateId) {
            props.apiRequest(
                summerFunnelService.updateAnswers,
                { stateId },
                {
                    objective,
                    niveauActuel,
                    // Re-send the class so it's persisted even if the class step's
                    // own save was skipped (funnelStateId not yet synced there).
                    studentClass: state.studentClass,
                    studentClassRentree: state.studentClassRentree,
                }
            )
        }
        amplitude.track("summer_funnel_diagnostic_completed", {
            funnel_state_id: stateId,
            objective,
        })
        navigation.navigate("summer/configurator", nextParams)
    }

    const goNext = () => {
        if (subStep < STEP_NIVEAU) {
            setSubStep((s) => s + 1)
        } else {
            submitDiagnostic()
        }
    }

    const goBack = () => {
        if (subStep > STEP_OBJECTIVE) {
            setSubStep((s) => s - 1)
        } else {
            navigation.goBack()
        }
    }

    const headerStep = DIAGNOSTIC_GLOBAL_STEP[subStep]

    // --- Sous-étape 1 : objectif (mono) ---
    if (subStep === STEP_OBJECTIVE) {
        return (
            <FunnelQuestionScreen
                illustration={objectifIllustration}
                illustrationBgColor={theme.colors.primaryLighter}
                question="Quel est l'objectif principal de cet été ?"
                options={SUMMER_OBJECTIVES.map((o) => ({
                    id: o.id,
                    label: o.label,
                    subLabel: o.subLabel,
                    emoji: o.emoji,
                }))}
                selectedIds={objective ? [objective] : []}
                onToggle={(id) => setObjective(id)}
                ctaLabel="Continuer"
                onContinue={goNext}
                step={headerStep}
                total={FUNNEL_TOTAL_STEPS}
                onBack={goBack}
            />
        )
    }

    // --- Sous-étape 2 : niveau ressenti (mono) ---
    return (
        <FunnelQuestionScreen
            illustration={niveauIllustration}
            illustrationBgColor={theme.colors.primaryLighter}
            question="Comment se situe votre enfant cette année ?"
            options={SUMMER_NIVEAUX.map((n) => ({
                id: n.id,
                label: n.label,
                emoji: n.emoji,
            }))}
            selectedIds={niveauActuel ? [niveauActuel] : []}
            onToggle={(id) => setNiveauActuel(id)}
            ctaLabel="Continuer"
            onContinue={goNext}
            step={headerStep}
            total={FUNNEL_TOTAL_STEPS}
            onBack={goBack}
        />
    )
}

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.summerFunnel?.funnelStateId ?? null,
})

const mapDispatchToProps = { apiRequest }

export default connect(mapStateToProps, mapDispatchToProps)(SummerDiagnosticScreen)
